import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import { API_URL } from "@/config";
import { CheckCircle, Zap, Star, Shield, Loader2, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

const PACKAGES = [
    {
        id: "BRONZE",
        name: "Bronze",
        price: "$99",
        period: "/mo",
        leads: "25 leads",
        icon: Shield,
        color: "text-amber-700",
        bg: "bg-amber-50",
        border: "border-amber-200",
        features: ["Standard leads", "Email & phone", "Basic filtering", "7-day freshness"],
    },
    {
        id: "SILVER",
        name: "Silver",
        price: "$249",
        period: "/mo",
        leads: "75 leads",
        icon: Star,
        color: "text-slate-600",
        bg: "bg-slate-50",
        border: "border-slate-300",
        features: ["Premium leads", "Email & phone", "Advanced filtering", "3-day freshness", "Vehicle intent data"],
        popular: true,
    },
    {
        id: "GOLD",
        name: "Gold",
        price: "$499",
        period: "/mo",
        leads: "200 leads",
        icon: Zap,
        color: "text-yellow-600",
        bg: "bg-yellow-50",
        border: "border-yellow-300",
        features: ["Exclusive leads", "Email, phone & chat", "Full targeting suite", "Same-day freshness", "Credit pre-screen", "BDC follow-up included"],
    },
];

export default function DealerLeadsBuyPage() {
    const { user }    = useAuth();
    const navigate    = useNavigate();
    const dealerId    = user?.dealerId ? Number(user.dealerId) : 0;

    const [selected,    setSelected]    = useState("SILVER");
    const [firstName,   setFirstName]   = useState("");
    const [lastName,    setLastName]    = useState("");
    const [email,       setEmail]       = useState("");
    const [phone,       setPhone]       = useState("");
    const [leadType,    setLeadType]    = useState("AUTO");
    const [notes,       setNotes]       = useState("");
    const [submitting,  setSubmitting]  = useState(false);
    const [success,     setSuccess]     = useState(false);
    const [error,       setError]       = useState<string | null>(null);

    const pkg = PACKAGES.find((p) => p.id === selected)!;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!firstName || !lastName || !email) return;
        setSubmitting(true);
        setError(null);
        try {
            await http.post("/leads", {
                cust_fname:       firstName,
                cust_lname:       lastName,
                cust_email:       email,
                cust_phoneno:     phone || null,
                cust_lead_type:   `PACKAGE_REQUEST_${selected}`,
                cust_lead_source: "DEALER_PORTAL",
                cust_dealer_id:   dealerId,
                cust_comment:     `Package: ${pkg.name} · Lead type preference: ${leadType}${notes ? ` · Notes: ${notes}` : ""}`,
                cust_leadcost:    0,
            });
            setSuccess(true);
        } catch {
            setError("Submission failed — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-lg mx-auto mt-20 text-center space-y-4 p-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">Request Submitted!</h2>
                <p className="text-sm text-muted-foreground">
                    Your <strong>{pkg.name} package</strong> request has been received. Our BDC team will contact you within 1 business day to confirm and activate your leads.
                </p>
                <Button onClick={() => navigate("/dealer/leads")} className="mt-4">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Leads
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-8 p-6 max-w-4xl mx-auto">
            <div>
                <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
                    <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <h1 className="text-2xl font-semibold">Buy Leads</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    Select a lead package and complete the request form — our BDC team activates your account within 1 business day.
                </p>
            </div>

            {/* Package selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {PACKAGES.map((p) => {
                    const Icon = p.icon;
                    const active = selected === p.id;
                    return (
                        <Card
                            key={p.id}
                            onClick={() => setSelected(p.id)}
                            className={`p-5 cursor-pointer transition-all relative ${active ? `ring-2 ring-indigo-500 ${p.border}` : "hover:shadow-md"}`}
                        >
                            {p.popular && (
                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full font-medium">
                                    Most Popular
                                </span>
                            )}
                            <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center mb-3`}>
                                <Icon className={`h-5 w-5 ${p.color}`} />
                            </div>
                            <p className="font-semibold">{p.name}</p>
                            <p className="text-2xl font-bold mt-1">{p.price}<span className="text-sm font-normal text-muted-foreground">{p.period}</span></p>
                            <p className="text-sm text-muted-foreground mt-0.5">{p.leads}</p>
                            <ul className="mt-4 space-y-1.5">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    );
                })}
            </div>

            {/* Request form */}
            <Card className="p-6">
                <h2 className="font-semibold mb-4">Complete Your Request — {pkg.name} Package</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">First Name *</label>
                            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required placeholder="John" />
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">Last Name *</label>
                            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required placeholder="Smith" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">Email *</label>
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="contact@dealership.com" />
                        </div>
                        <div>
                            <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">Phone</label>
                            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 000-0000" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">Lead Type Preference</label>
                        <select
                            value={leadType}
                            onChange={(e) => setLeadType(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="AUTO">Auto / New Vehicle</option>
                            <option value="USED">Used Vehicle</option>
                            <option value="FINANCE">Finance / Credit</option>
                            <option value="TRADE">Trade-In</option>
                            <option value="SERVICE">Service / Repair</option>
                            <option value="MIXED">Mixed</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-medium uppercase text-muted-foreground block mb-1">Additional Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Target geography, vehicle types, budget range…"
                            className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                        />
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                        {submitting
                            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting…</>
                            : `Request ${pkg.name} Package — ${pkg.price}${pkg.period}`
                        }
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                        No immediate charge. Our BDC team will confirm terms and billing before activation.
                    </p>
                </form>
            </Card>
        </div>
    );
}
